/**
 * Team settings' People: who is on the Team. A draft like every other field,
 * written by Save (`settingsDraft.saveTeamSettings`): adds first, removals
 * last. The owner cannot be removed; handing the Team on is its own act.
 */
import { Btn } from "../../queue/index.js";
import { Face } from "../people/Face.js";
import { PEOPLE_SETTINGS_HELP } from "../people/people.js";
import "../people/people.css";
import type { SettingsCtx } from "./context.js";
import { Field } from "./fields.js";

export function PeopleField({ ctx, disabled }: { ctx: SettingsCtx; disabled: boolean }) {
  const { team, page } = ctx;
  const ownerId =
    page.members.find((member) => member.role === "owner")?.userId ??
    page.team?.publisherUserId ??
    null;
  const personOf = (userId: string) => {
    const member = page.members.find((entry) => entry.userId === userId);
    const person = ctx.people.find((entry) => entry.userId === userId);
    return {
      name: person?.name ?? member?.displayName?.trim() ?? member?.email ?? userId,
      email: member?.email ?? person?.email ?? null,
      avatarUrl: member?.avatarUrl ?? person?.avatarUrl ?? null,
    };
  };
  const addable = ctx.people.filter((person) => !team.people.includes(person.userId));

  const remove = (userId: string) =>
    ctx.setTeam((draft) => ({
      ...draft,
      people: draft.people.filter((entry) => entry !== userId),
      // Off the Team is off who it asks, the way the server does it.
      asks: draft.asks.filter((row) => row.userId !== userId),
    }));

  return (
    <Field label="People" top help={PEOPLE_SETTINGS_HELP} testId="settings-team-people">
      <div className="peoplefield">
        {team.people.map((userId) => {
          const person = personOf(userId);
          const owner = userId === ownerId;
          const theirTickets = team.whose === userId;
          return (
            <div className="prow" key={userId} data-testid="settings-person">
              <Face userId={userId} name={person.name} avatarUrl={person.avatarUrl} size={28} />
              <span className="who">
                <span className="nm">{person.name}</span>
                {person.email && <span className="em">{person.email}</span>}
              </span>
              <span className={`chip role${owner ? " owner" : ""}`}>
                {owner ? "Owner" : "Member"}
              </span>
              {!disabled && !owner && (
                <Btn
                  variant="quiet"
                  size="sm"
                  aria-label={`Remove ${person.name}`}
                  disabled={theirTickets}
                  title={
                    theirTickets
                      ? "The Team works from their tickets. Change Whose tickets first."
                      : undefined
                  }
                  onClick={() => remove(userId)}
                >
                  Remove
                </Btn>
              )}
            </div>
          );
        })}
        {!disabled && addable.length > 0 && (
          <select
            aria-label="Add a person to the Team"
            value=""
            style={{ maxWidth: 220 }}
            data-testid="settings-team-people-add"
            onChange={(event) => {
              const userId = event.target.value;
              if (userId) ctx.setTeam((draft) => ({ ...draft, people: [...draft.people, userId] }));
            }}
          >
            <option value="">Add a person</option>
            {addable.map((person) => (
              <option key={person.userId} value={person.userId}>
                {person.name}
              </option>
            ))}
          </select>
        )}
      </div>
    </Field>
  );
}
